import { useEffect, useRef, useState, type ImgHTMLAttributes } from "react";
import { observeNear } from "@/lib/viewport-anticipation";

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, "loading"> & {
  src: string;
  alt: string;
  width: number;
  height: number;
  /** The single LCP image of a route — loads eagerly at high priority. */
  priority?: boolean;
  /** Base lookahead band in px; scaled up while the user scrolls fast. */
  band?: number;
  /** Skip the fade — useful for tiny logos and icons. */
  noFade?: boolean;
};

/**
 * A below-the-fold image that is already decoded by the time it scrolls in.
 *
 * Lazy by default. Once the element enters a velocity-scaled proximity band,
 * the image is promoted to an immediate fetch and decoded off the main
 * thread; only then does it fade in, so no half-painted image is ever shown.
 * Width/height are required so the slot is reserved and CLS stays at zero.
 */
export function SmartImage({
  src,
  alt,
  width,
  height,
  priority = false,
  band = 800,
  noFade = false,
  className,
  style,
  ...rest
}: Props) {
  const ref = useRef<HTMLImageElement>(null);
  const [ready, setReady] = useState(priority || noFade);
  // Cached images paint instantly, with no fade from an empty slot.
  const [instant, setInstant] = useState(false);

  useEffect(() => {
    const img = ref.current;
    if (img?.complete && img.naturalWidth > 0) {
      setInstant(true);
      setReady(true);
    }
  }, [src]);

  useEffect(() => {
    if (priority) return;
    const img = ref.current;
    if (!img) return;

    const warm = () => {
      if (img.loading === "lazy") img.loading = "eager";
      img.fetchPriority = "auto";
      if (img.complete && img.naturalWidth > 0) {
        setReady(true);
        return;
      }
      img.decode?.().then(
        () => setReady(true),
        () => setReady(true),
      );
    };

    return observeNear(img, band, warm);
  }, [priority, band, src]);

  return (
    <img
      ref={ref}
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? "eager" : "lazy"}
      decoding={priority ? "sync" : "async"}
      fetchPriority={priority ? "high" : "low"}
      onLoad={() => setReady(true)}
      className={className}
      style={
        noFade || instant
          ? style
          : {
              ...style,
              opacity: ready ? 1 : 0,
              transition: "opacity 220ms ease",
            }
      }
      {...rest}
    />
  );
}
